import React, { useState, useEffect } from "react";
import { 
  Plus, Edit, Trash2, Search, ArrowLeft, RefreshCw, 
  CheckCircle, AlertCircle, FileText 
} from "lucide-react";
import axios from "axios";

export default function BlogManager() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusMessage, setStatusMessage] = useState(null);

  // Form State (Aligned with Database Schema)
  const [formData, setFormData] = useState({
  title: "",
  slug: "",
  category: "General",
  content: "",
  excerpt: "",
  author: "Admin",
  coverImage: "",
  status: "published",
  tags: [] // Pass explicit empty array if tags are unused in form
});

  const API_URL = process.env.REACT_APP_API_URL || "https://api.indivtechnosoft.com/api";

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/blogs`);
      // FIX: Extract array from API wrapper response
      const blogList = Array.isArray(res.data) 
        ? res.data 
        : Array.isArray(res.data?.data) 
        ? res.data.data 
        : [];
      setBlogs(blogList);
    } catch (err) {
      showStatus("Failed to fetch blog posts", "error");
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const showStatus = (text, type = "success") => {
    setStatusMessage({ text, type });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        status: checked ? "published" : "draft",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const generateSlug = () => {
    if (!formData.title) return;
    const slug = formData.title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setFormData((prev) => ({ ...prev, slug }));
  };

  const handleEdit = (blog) => {
    setEditingId(blog.id || blog._id);
    setFormData({
      title: blog.title || "",
      slug: blog.slug || "",
      category: blog.category || "",
      content: blog.content || "",
      excerpt: blog.excerpt || blog.summary || "",
      author: blog.author || "Admin",
      coverImage: blog.coverImage || blog.featuredImage || "",
      status: blog.status || (blog.isPublished ? "published" : "draft"),
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog post?")) return;

    try {
      await axios.delete(`${API_URL}/blogs/${id}`);
      setBlogs((prev) => (Array.isArray(prev) ? prev.filter((b) => (b.id || b._id) !== id) : []));
      showStatus("Blog post deleted successfully");
    } catch (err) {
      showStatus("Failed to delete blog post", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      showStatus("Title and Content are required", "error");
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_URL}/blogs/${editingId}`, formData);
        showStatus("Blog post updated successfully");
      } else {
        await axios.post(`${API_URL}/blogs`, formData);
        showStatus("Blog post created successfully");
      }
      resetForm();
      fetchBlogs();
    } catch (err) {
      showStatus(editingId ? "Failed to update blog" : "Failed to create blog", "error");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      category: "",
      content: "",
      excerpt: "",
      author: "Admin",
      coverImage: "",
      status: "published",
    });
    setEditingId(null);
    setIsFormOpen(false);
  };

  // Safe filtering check
  const blogArray = Array.isArray(blogs) ? blogs : [];
  const filteredBlogs = blogArray.filter((b) =>
    b.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Alert Status Banner */}
      {statusMessage && (
        <div
          className={`p-4 rounded-xl border flex items-center gap-3 transition-all ${
            statusMessage.type === "error"
              ? "bg-rose-50 border-rose-200 text-rose-700"
              : "bg-emerald-50 border-emerald-200 text-emerald-700"
          }`}
        >
          {statusMessage.type === "error" ? (
            <AlertCircle size={20} />
          ) : (
            <CheckCircle size={20} />
          )}
          <span className="text-sm font-semibold">{statusMessage.text}</span>
        </div>
      )}

      {/* Action Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Content Studio / Blog Hub
          </h2>
          <p className="text-xs text-slate-500 font-medium mt-1">
            Publish and manage enterprise knowledge bases & articles.
          </p>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            onClick={fetchBlogs}
            className="p-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition-all border border-slate-200"
            title="Refresh List"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>

          {!isFormOpen && (
            <button
              onClick={() => {
                resetForm();
                setIsFormOpen(true);
              }}
              className="flex items-center gap-2 bg-[#194393] hover:bg-blue-800 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-all shadow-sm"
            >
              <Plus size={16} /> Create Article
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {isFormOpen ? (
        /* Form View */
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
          <div className="flex justify-between items-center border-b border-slate-100 pb-4">
            <h3 className="text-base font-bold text-slate-800">
              {editingId ? "Edit Article Node" : "Publish New Article"}
            </h3>
            <button
              onClick={resetForm}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 font-semibold"
            >
              <ArrowLeft size={14} /> Back to Hub
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  onBlur={generateSlug}
                  placeholder="Article Headline"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-600"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">URL Slug</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="article-headline-slug"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g. Engineering, Tech"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-600"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Author</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-600"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Cover Image URL</label>
                <input
                  type="text"
                  name="coverImage"
                  value={formData.coverImage}
                  onChange={handleInputChange}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Excerpt / Summary</label>
              <textarea
                name="excerpt"
                rows={2}
                value={formData.excerpt}
                onChange={handleInputChange}
                placeholder="Brief excerpt for card preview..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-600"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Content (Markdown / HTML)</label>
              <textarea
                name="content"
                rows={8}
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Write full article body here..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 font-mono"
                required
              />
            </div>

            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="isPublished"
                name="isPublished"
                checked={formData.status === "published"}
                onChange={handleInputChange}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="isPublished" className="text-xs font-bold text-slate-700">
                Publish Immediately
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-[#194393] hover:bg-blue-800 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
              >
                {editingId ? "Save Changes" : "Create Post"}
              </button>
            </div>
          </form>
        </div>
      ) : (
        /* Article Table View */
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Filter Bar */}
          <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:border-blue-600"
              />
            </div>
            <span className="text-xs font-bold text-slate-400">
              Showing {filteredBlogs.length} articles
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50 text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                  <th className="py-3 px-4">Article</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">Author</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-400 font-medium">
                      Loading articles...
                    </td>
                  </tr>
                ) : filteredBlogs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-400 font-medium">
                      No blog posts found.
                    </td>
                  </tr>
                ) : (
                  filteredBlogs.map((blog) => {
                    const id = blog.id || blog._id;
                    const isPublished = blog.status === "published" || blog.isPublished === true;
                    const imgUrl = blog.coverImage || blog.featuredImage;

                    return (
                      <tr key={id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="py-3 px-4 font-semibold text-slate-900 flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 text-slate-400 overflow-hidden">
                            {imgUrl ? (
                              <img src={imgUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <FileText size={14} />
                            )}
                          </div>
                          <div>
                            <p className="line-clamp-1">{blog.title}</p>
                            <p className="text-[10px] text-slate-400 font-mono mt-0.5">{blog.slug}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-medium">{blog.category || "General"}</td>
                        <td className="py-3 px-4 text-slate-500">{blog.author || "Admin"}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              isPublished
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                            }`}
                          >
                            {isPublished ? "Published" : "Draft"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right space-x-2">
                          <button
                            onClick={() => handleEdit(blog)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 rounded hover:bg-slate-100 transition-all"
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(id)}
                            className="p-1.5 text-slate-500 hover:text-rose-600 rounded hover:bg-slate-100 transition-all"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}